import { SessionTimePreferences } from './account';
import { Components } from './payment';
import { FullPlanLabel } from './plan';

export enum OnSkipAction {
  FunnelDone = 'funnelDone',
}

export interface Step {
  name: string;
  cta?: string;
  id: string;
  nextStep?: string;
  isLastStep?: boolean;
  onSkipAction?: OnSkipAction;
}

export interface SignupStep extends Step {
  phoneVerification: boolean;
  testEmail: string;
}

export interface CirclesIntroductionStepV1 extends Step {
  pages: {
    image: string;
    title: string;
    description: string;
    cta: string;
  }[];
}

export interface CirclesIntroductionStepV2 extends Step {
  videoUrl: string;
  cta: string;
}

export type CirclesIntroductionStep = CirclesIntroductionStepV2 | CirclesIntroductionStepV1;

export interface SegmentToSelect {
  name: string;
  label: string;
  priority?: number;
  icon?: string;
}

export interface SelectSegmentsStep extends Step {
  title: string;
  subtitle?: string;
  segments: SegmentToSelect[];
}

export interface PurchaseStep extends Step {
  conditionalSegmentForDisplay: string[];
  premiumGFImage?: string;
  components: Components;
  componentsV2: Components;
}

export interface DiscoveryQuestionOption {
  name: string;
  label: string;
  nextStep: string;
  subtitle?: string;
}

export interface DiscoveryQuestionStep extends Step {
  key: string;
  title: string;
  subtitle?: string;
  options: DiscoveryQuestionOption[];
  cta: string;
  submitDiscovery?: boolean;
}

export interface SeparatorStep extends Step {
  title: string;
  subtitle?: string;
  cta: string;
  imageUrl: string;
  headline?: string;
  expertId?: string;
}

export interface SendMessageToExpertStep extends Step {
  headline?: string;
  title: string;
  subtitle?: string;
  textInputPlaceholder: string;
  cta: string;
  messageSentTitle: string;
  messageSentSubtitle: string;
  messageSentButton: string;
  expertId: string;
}

export interface QuoteStep extends Step {
  text: string;
  author: string;
  cta: string;
  imageUrl: string;
}

export type OnboardingPlanLabel = Pick<FullPlanLabel, 'id' | 'name' | 'displayCriteria'>;

export interface PlanLabelsStep extends Step {
  title: string;
  subtitle?: string;
  minLabelsSelected: number;
  planLabels: OnboardingPlanLabel[];
}

export interface TimePreferenceSelect {
  label: string;
  value: SessionTimePreferences;
}

export interface TimePreferenceStep extends Step {
  title: string;
  subtitle?: string;
  timePreferences: TimePreferenceSelect[];
}

export interface PlanPreviewStep extends Step {
  title: string;
  subtitle?: string;
  loaderText?: string;
}

export interface OnboardingIntroBenefit {
  text: string;
}

export interface OnboardingIntroStep extends Step {
  title: string;
  subtitle?: string;
  imageUrl?: string;
  benefits: OnboardingIntroBenefit[];
}

export interface OnboardingStartStep extends Step {
  title: string;
  subtitle?: string;
  imageUrl?: string;
}

export interface OnboardingGuide {
  avatar: string;
  name: string;
  title: string;
}

export interface GuidesPreviewStep extends Step {
  header: string;
  title: string;
  loaderTitle: string;
  loaderSubtitle: string;
  guides: OnboardingGuide[];
}

export interface OnboardingGoal {
  title: string;
  description: string;
}

export interface GoalsPreviewStep extends Step {
  header: string;
  title: string;
  loaderTitle: string;
  loaderSubtitle: string;
  goals: OnboardingGoal[];
}

export interface QuestionMetadata {
  criteriaType: string;
  section: string;
  nextStepId?: string;
  responseMapping?: { [key: string]: string };
  label?: string;
  mode?: string;
  quote?: string;
  quoteSubtitle?: string;
  imgMobile?: string;
}

export interface Question {
  id: string;
  stepType: string;
  title: string;
  description: string;
  canGoBack: boolean;
  ctaButtonTitle: string;
  metaData: QuestionMetadata;
}

export interface Criteria {
  type: string;
  name: string;
  label: string;
  status: string;
  id: string;
  lastUpdated: string;
  origin: string;
  meta: {
    iconUrl: string;
    relatedSegments: string[];
    relatedPlanLabels: string[];
  };
  priority: number;
  description: string;
}

export interface QuizStep extends Step {
  questions: Question[];
  criteria: Criteria[];
}

export interface ChooseSegmentStep extends Step {
  title: string;
  subtitle: string;
  backgroundImageUrl: string;
  segments: { name: string; label: string; icon: string }[];
  otherTitle: string;
  otherSubtitle1: string;
  otherSubtitle2: string;
  otherEmail: string;
  otherCtaText: string;
  otherBackgroundImageUrl: string;
}

export interface ChooseFocusAreaStep extends Step {
  title: string;
  subtitle: string;
  labels: Record<string, Pick<FullPlanLabel, 'id' | 'name' | 'displayCriteria'>[]>;
  backgroundImageUrl: string;
  ctaText: string;
}

export interface WalkthrougTransitionStep extends Step {
  title: string;
  subtitle: string;
  backgroundImageUrl: string;
  ctaText: string;
}

export interface HowItWorksStep extends Step {
  header: string;
  title: string;
  imageUrl?: Record<string, string>;
  guides?: {
    label: string;
    imageUrl: string;
    name: string;
    description: string;
    ratingText: string;
    ratingNumber: string;
    reviewsText: string;
    reviewsNumber: string;
    sessionsText: string;
    sessionsNumber: string;
    audioText: string;
    audioUrl: Record<string, string>;
  }[];
  moreGuidesText?: string;
  moreGuidesTopImageUrl?: string;
  moreGuidesBottomImageUrl?: string;
  subtitle: string;
  ctaText: string;
}

export interface PaymentSuccessStep extends Step {
  title: string;
  subtitle: string;
  backgroundImageUrl: string;
  ctaText: string;
}

export type MobileSignupSteps = Array<
  | SignupStep
  | CirclesIntroductionStep
  | SelectSegmentsStep
  | PurchaseStep
  | DiscoveryQuestionStep
  | QuoteStep
  | SeparatorStep
  | SendMessageToExpertStep
  | PlanLabelsStep
  | TimePreferenceStep
  | PlanPreviewStep
  | OnboardingIntroStep
  | OnboardingStartStep
  | GuidesPreviewStep
  | GoalsPreviewStep
  | QuizStep
  | ChooseSegmentStep
  | ChooseFocusAreaStep
  | WalkthrougTransitionStep
  | HowItWorksStep
  | PaymentSuccessStep
>;
